/**
 * SiteBreadcrumbs — فتات خبز موحَّدة لكامل الموقع.
 * - يقرأ pathname من useLocation
 * - يبني السلسلة من BREADCRUMBS (parent chain)
 * - يدعم RTL: الفاصل يتقلّب آلياً (› في en، ‹ في ar)
 * - يحقن JSON-LD BreadcrumbList للصفحات العامّة فقط (scope="public")
 * - يُخفى تلقائياً على الجذور (/, /admin, /account) لتفادي عنصر واحد
 */
import * as React from "react";
import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ChevronLeft, ChevronRight, Home } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { BREADCRUMBS, humanize, type Crumb } from "@/lib/breadcrumbsMap";
import { setSmartNavLabel, getSmartNavLabel, getSmartNavStack } from "@/lib/smartBack";
import { cn } from "@/lib/utils";
import { PUBLIC_SITE_ORIGIN } from "@/lib/public-origin";


type Scope = "public" | "admin" | "account";

interface Props {
  scope: Scope;
  /** عنوان مخصَّص للعنصر الأخير (للصفحات الديناميكيّة: منتج/مقال/مجموعة) */
  currentLabel?: string;
  className?: string;
  /**
   * فلتر صلاحيّات الأدمن — يُمرَّر من غلاف admin فقط (AdminNav).
   * فصله هنا يمنع سحب useAdminNavPermissions وسلسلة الأدمن إلى حزمة الزائر.
   */
  canSee?: (path: string) => boolean;
}

const PUBLIC_ORIGIN = PUBLIC_SITE_ORIGIN;

function normalize(p: string) {
  return p.replace(/\/+$/, "") || "/";
}

function labelFor(path: string, isAr: boolean, dynamicLabel?: string): string {
  const entry = BREADCRUMBS[path];
  if (entry) return isAr ? entry.ar : entry.en;
  if (dynamicLabel) return dynamicLabel;
  const stored = getSmartNavLabel(path);
  if (stored) return stored;
  const seg = path.split("/").filter(Boolean).pop() || "";
  return humanize(decodeURIComponent(seg));
}

/**
 * سلسلة parents الثابتة من BREADCRUMBS (fallback عند غياب مكدّس تنقّل).
 */
function staticChain(pathname: string, currentLabel?: string): Array<{ path: string; label: string }> {
  const clean = normalize(pathname);
  const chain: Array<{ path: string; label: string; entry?: Crumb }> = [];
  const last = BREADCRUMBS[clean];

  if (last) {
    let cursor: string | null | undefined = clean;
    const seen = new Set<string>();
    while (cursor && !seen.has(cursor)) {
      seen.add(cursor);
      const entry: Crumb | undefined = BREADCRUMBS[cursor];
      if (!entry) break;
      chain.unshift({ path: cursor, label: "", entry });
      cursor = entry.parent;
    }
  } else {
    const segments = clean.split("/").filter(Boolean);
    chain.push({ path: "/", label: "", entry: BREADCRUMBS["/"] });
    let acc = "";
    for (let i = 0; i < segments.length; i++) {
      acc += "/" + segments[i];
      const known = BREADCRUMBS[acc];
      if (known) {
        chain.push({ path: acc, label: "", entry: known });
      } else if (i === segments.length - 1) {
        chain.push({ path: acc, label: currentLabel || "" });
      }
    }
  }

  return chain.map((c, i) => ({
    path: c.path,
    label: c.label || (i === chain.length - 1 && currentLabel ? currentLabel : ""),
  }));
}

function resolveChain(pathname: string, currentLabel?: string): Array<{ path: string; label: string }> {
  // نستخدم دائماً السلسلة الهرميّة الثابتة (parent chain) — الـbreadcrumbs
  // تعكس بنية الموقع لا تاريخ التنقّل. الرجوع التسلسليّ مسؤوليّة زرّ الرجوع.
  return staticChain(pathname, currentLabel);
}

export function SiteBreadcrumbs({ scope, currentLabel, className, canSee }: Props) {
  const { pathname } = useLocation();
  const router = useRouter();
  const { locale } = useLocale();
  const isAr = locale === "ar";

  const homeBlocksReturnSearch = React.useMemo(() => {
    if (scope !== "admin" || pathname !== "/admin/collection-overlays") return undefined;
    if (typeof window === "undefined") return undefined;
    const params = new URLSearchParams(window.location.search);
    if (params.get("from") !== "home-blocks") return undefined;
    const handle = params.get("handle");
    if (!handle) return undefined;
    const device = params.get("device");
    return {
      edit: handle,
      device: device === "desktop" || device === "mobile" ? device : undefined,
    };
  }, [pathname, scope]);

  // فلتر صلاحيّات الأدمن — يأتي جاهزاً من الغلاف؛ الافتراضي يسمح بكلّ شيء
  const canSeePath = React.useCallback(
    (p: string) => {
      if (scope !== "admin" || !canSee) return true;
      if (p === "/" || p === "/admin") return true;
      return canSee(p);
    },
    [canSee, scope],
  );

  // سجّل عنوان الصفحة الحاليّة في مخزن smartBack ليُستعمل لاحقاً
  // كتسمية للصفحة السابقة في breadcrumbs عند التنقّل التسلسليّ.
  React.useEffect(() => {
    if (currentLabel) setSmartNavLabel(pathname, currentLabel);
  }, [pathname, currentLabel]);

  const rawChain = resolveChain(pathname, currentLabel);

  // املأ الـlabels: BREADCRUMBS أوّلاً، ثمّ التسميات المخزّنة، ثمّ humanize
  const chain = rawChain.map((c) => {
    if (c.label) return c;
    return { path: c.path, label: labelFor(c.path, isAr) };
  });

  // اخفِ إذا عنصر واحد فقط أو لا شيء
  if (chain.length < 2) return null;
  const Separator = isAr ? ChevronLeft : ChevronRight;

  // JSON-LD للصفحات العامّة فقط
  const jsonLd =
    scope === "public"
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: chain.map((c, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: c.label,
            item: `${PUBLIC_ORIGIN}${c.path === "/" ? "" : c.path}`,
          })),
        }
      : null;

  // على الموبايل نقلّص السلسلة الطويلة (>3) إلى: الجذر › … › الأب › الحاليّ
  // ونحتفظ بالكامل من sm فما فوق. نستخدم عنصرَين متوازيَين (mobile/desktop)
  // لتفادي حسابات JS ومنع أيّ قصّ غامض.
  const collapsed = chain.length > 3
    ? [chain[0], { path: "__ellipsis__", label: "…" } as { path: string; label: string }, chain[chain.length - 2], chain[chain.length - 1]]
    : chain;

  const parentPath = chain[chain.length - 2]?.path;
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  const goBack = () => {
    if (typeof window === "undefined") return;
    const stack = getSmartNavStack();
    if (stack.length >= 2 && window.history.length > 1) {
      window.history.back();
      return;
    }
    if (parentPath) router.navigate({ to: parentPath as never });
  };

  const backButton = (
    <button
      type="button"
      onClick={goBack}
      className="me-1 inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-border bg-background px-2.5 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={isAr ? "رجوع للصفحة السابقة" : "Back to previous page"}
      title={isAr ? "رجوع للصفحة السابقة" : "Back to previous page"}
    >
      <BackIcon className="h-3 w-3" aria-hidden />
      <span>{isAr ? "رجوع" : "Back"}</span>
    </button>
  );

  const renderList = (items: typeof chain, keyPrefix: string) => (
    <ol className="flex items-center gap-1.5">
      {items.map((c, i) => {
        const isLast = i === items.length - 1;
        const isHomeRoot = i === 0 && c.path === "/";
        const isEllipsis = c.path === "__ellipsis__";
        const allowed = canSeePath(c.path);
        const search = c.path === "/admin/home-blocks" ? homeBlocksReturnSearch : undefined;
        return (
          <li key={keyPrefix + c.path + i} className="inline-flex items-center gap-1.5">
            {i > 0 && <Separator className="h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />}
            {isEllipsis ? (
              <span className="px-0.5 text-muted-foreground/70" aria-hidden>…</span>
            ) : isLast || !allowed ? (
              <span
                className={cn(
                  "max-w-[46vw] truncate sm:max-w-none",
                  isLast ? "font-medium text-foreground" : "text-muted-foreground/60"
                )}
                aria-current={isLast ? "page" : undefined}
                title={!allowed && !isLast ? (isAr ? "لا تملك صلاحيّة فتح هذا القسم" : "No permission to open") : c.label}
              >
                {c.label}
              </span>
            ) : (
              <Link
                to={c.path}
                search={search as never}
                className="inline-flex min-h-[28px] max-w-[38vw] items-center gap-1 truncate rounded px-1 py-1 text-muted-foreground transition-colors hover:text-foreground hover:underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:text-foreground sm:max-w-none"
                title={c.label}
              >
                {isHomeRoot && <Home className="h-3.5 w-3.5 shrink-0" aria-hidden />}
                <span className="truncate">{c.label}</span>
              </Link>
            )}
          </li>
        );
      })}
    </ol>
  );

  return (
    <>
      <nav
        aria-label="breadcrumb"
        className={cn(
          "w-full text-[13px] leading-5 text-muted-foreground sm:text-xs",
          "[mask-image:linear-gradient(to_left,transparent,black_16px)] rtl:[mask-image:linear-gradient(to_right,transparent,black_16px)]",
          className,
        )}
      >
        {/* موبايل: سلسلة مختصرة بلا تمرير أفقيّ */}
        <div className="flex items-center overflow-hidden sm:hidden">
          {backButton}
          {renderList(collapsed, "m-")}
        </div>
        {/* من sm فأكبر: السلسلة الكاملة مع تمرير أفقيّ آمن */}
        <div className="hidden items-center overflow-x-auto whitespace-nowrap sm:flex">
          {backButton}
          {renderList(chain, "d-")}
        </div>
      </nav>
      {jsonLd && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      )}
    </>
  );
}



